import { JSONSchema7 } from 'json-schema';

import { MockDatabase } from "./database/MockDatabase";
import { MockAttributions } from "./database/tables/attributions.mockTable";
import { MockBundles } from "./database/tables/bundles.mockTable";
import { MockBundlesOrganizationsAttributons } from "./database/tables/bundles_organizations_attributions.mockTable";
import { MockLinks } from "./database/tables/links.mockTable";
import { MockOrganizations } from "./database/tables/organizations.mockTable";
import { MockOrganizationsAttributons } from "./database/tables/organizations_attributions.mockTable";
import { MockSchemaData } from "./database/tables/schema_data.mockTable";
import { MockSchemaType, SchemaTypeAPI } from "./database/tables/schema_type.mockTable";
import { MockSchemaTypeName } from "./database/tables/schema_type_name.mockTable";

import { MOCK_DATA as MOCK_ATTRIBUTIONS } from "./data/attributions.mockData";
import { MOCK_DATA as MOCK_BUNDLES_ORGANIZATIONS_ATTRIBUTIONS } from "./data/bundles_organizations_attributions.mockData";
import { MOCK_DATA as MOCK_BUNDLES } from "./data/bundles.mockData";
import { MOCK_DATA as MOCK_LINKS } from "./data/links.mockData";
import { MOCK_DATA as MOCK_ORGANIZATIONS_ATTRIBUTIONS } from "./data/organizations_attributions.mockData";
import { MOCK_DATA as MOCK_ORGANIZATIONS } from "./data/organizations.mockData";
import { MOCK_DATA as MOCK_SCHEMA_DATA } from "./data/schema_data.mockData";
import { MOCK_DATA as MOCK_SCHEMA_TYPE_NAME } from "./data/schema_type_name.mockData";
import { MOCK_DATA as MOCK_SCHEMA_TYPE } from "./data/schema_type.mockData";
import { MOCKED_ATTRIBUTION_SCHEMAS_FROM_FILE } from './data/attribution_schemas.mockData';
import { MOCKED_QE_SCHEMAS_FROM_FILE } from './data/qe_schemas.mockData';
import { MockCDN } from './database/tables/cdn.mockTable';
import { Attribution } from '../attribution/types';
import { MockDataForRequestManager } from '../RequestManager.class';

export const ATTRIBUTION_SCHEMA_TYPES: string[] = [
    "AGGREGATOR",
    "CAMPAIGN",
    "PARTNER",
    "CAMPAIGN_MEDIUM",
    "CAMPAIGN_SOURCE",
    "CAMPAIGN_CONTENT",
    "BORROWER",
    "AGENT_ID",
    "AGENT_NAME",
    "ORIGINATING_CAMPAIGN",
    "SEMSEE_IDENTIFICATION",
    "PARTNER",
    "CAMPAIGN",
];

export const QE_SCHEMA_TYPES: string[] = [
    "QUOTE_EXPERIENCE_COLORS_CONTENT",
    "QUOTE_EXPERIENCE_TYPOGRAPHY",
    "QUOTE_EXPERIENCE_COLORS",
    "QUOTE_EXPERIENCE_CONFIG",
    "QUOTE_EXPERIENCE_LOCALIZATIONS",
    "QUOTE_EXPERIENCE_ICONS",
    "QUOTE_EXPERIENCE_CONFIG",
    "QUOTE_EXPERIENCE_CONFIG",
    "QUOTE_EXPERIENCE_CONFIG",
    "QUOTE_EXPERIENCE_IMAGES",
    "QUOTE_EXPERIENCE_LOCALIZATIONS",
    "QUOTE_EXPERIENCE_TYPOGRAPHY",
    "QUOTE_EXPERIENCE_CONFIG",
    "QUOTE_EXPERIENCE_CONFIG",
    "QUOTE_EXPERIENCE_IMAGES",
    "QUOTE_EXPERIENCE_LOCALIZATIONS",
    "QUOTE_EXPERIENCE_IMAGES",
    "QUOTE_EXPERIENCE_CONFIG",
    "QUOTE_EXPERIENCE_TYPOGRAPHY",
    "QUOTE_EXPERIENCE_CONFIG",
    "QUOTE_EXPERIENCE_COLORS",
];

export class MockDatabaseCRUD {
    constructor() {
        this._db = new MockDatabase();
        this._db.addTable(new MockSchemaTypeName(this._db));
        this._db.addTable(new MockSchemaType(this._db));
        this._db.addTable(new MockSchemaData(this._db));
        this._db.addTable(new MockOrganizations(this._db));
        this._db.addTable(new MockAttributions(this._db));
        this._db.addTable(new MockOrganizationsAttributons(this._db));
        this._db.addTable(new MockBundles(this._db));
        this._db.addTable(new MockBundlesOrganizationsAttributons(this._db));
        this._db.addTable(new MockLinks(this._db));
        this._db.addTable(new MockCDN(this._db));
    }

    private _db: MockDatabase;
    private _seeded: boolean = false;
    public readonly seeded: boolean = this._seeded;

    public readonly db = () => this._db;

    public readonly seedDB = () => {
        this._seedSchemaData();
        const organizations = this._db.getTableByName('organizations');
        const attributions = this._db.getTableByName('attributions');
        const organizations_attributions = this._db.getTableByName('organizations_attributions');
        const bundles = this._db.getTableByName('bundles');
        const bundles_organizations_attributions = this._db.getTableByName('bundles_organizations_attributions');
        const links = this._db.getTableByName('links');
        organizations.populateData(MOCK_ORGANIZATIONS);
        attributions.populateData(MOCK_ATTRIBUTIONS);
        organizations_attributions.populateData(MOCK_ORGANIZATIONS_ATTRIBUTIONS);
        bundles.populateData(MOCK_BUNDLES);
        bundles_organizations_attributions.populateData(MOCK_BUNDLES_ORGANIZATIONS_ATTRIBUTIONS);
        links.populateData(MOCK_LINKS);
    }

    private _seedSchemaData = () => {
        const schema_type_name = this._db.getTableByName('schema_type_name');
        const schema_type = this._db.getTableByName('schema_type');
        const schema_data = this._db.getTableByName('schema_data');
        if (MOCK_SCHEMA_TYPE_NAME.length && MOCK_SCHEMA_TYPE.length) {
            schema_type_name.populateData(MOCK_SCHEMA_TYPE_NAME);
            schema_type.populateData(MOCK_SCHEMA_TYPE);
        } else {
            [
                ...MOCKED_QE_SCHEMAS_FROM_FILE,
                ...MOCKED_ATTRIBUTION_SCHEMAS_FROM_FILE
            ].forEach(mockedSchema => {
                const schemaTypeRecords = schema_type_name.query([
                    {
                        fieldName: 'schema_type',
                        value: mockedSchema['schema_type']
                    }
                ]);
                const schemaTypeRec = schemaTypeRecords.length ? schemaTypeRecords[0] : schema_type_name.createRecord({
                    schema_type: mockedSchema['schema_type']
                });
                schema_type.createRecord({
                    schema_type_name_id: schemaTypeRec.values['id'],
                    schema_type_version: mockedSchema['schema_type_version'],
                    schema: JSON.stringify(mockedSchema.schema)
                });
            });
        }
        schema_data.populateData(MOCK_SCHEMA_DATA);
    }

    public readonly resolveGuidToId = (tableName: string, guid: string) => {
        return this._db.resolveGuidToId(tableName, guid);
    }

    public readonly getSchemasByUsageType = (type: 'attribution' | 'quote-experience') => {
        const schema_type = this._db.getTableByName('schema_type');
        const types = type === 'attribution' ? ATTRIBUTION_SCHEMA_TYPES : QE_SCHEMA_TYPES;
        const allSchemaTypes = schema_type.all();
        const retSchemaTypes: SchemaTypeAPI[] = [];

        for (let schemaType of allSchemaTypes) {
            const schemaTypeName = schemaType.getRelated('schema_type_name');
            if (!schemaTypeName.length || types.indexOf(schemaTypeName[0].values['schema_type']) === -1) {
                continue;
            }
            const schemaTypeNameVal = schemaTypeName[0].values['schema_type'];
            let schema: JSONSchema7 | null = null;
            try {
                schema = JSON.parse(schemaType.values['schema']);
            } catch (e) {
                console.warn(`Failed to serialize schema for ${schemaTypeNameVal} version ${schemaType.values['schema_type_version']}`);
            }
            retSchemaTypes.push({
                schema_type: schemaTypeNameVal,
                schema_type_version: schemaType.values['schema_type_version'],
                schema: schema!
            });
        }

        return retSchemaTypes;
    }

    public createAttribution({ type, properties, displayName, status }: Omit<Attribution, "attributionGuid">): MockDataForRequestManager {

        try {
            let attrGuid: string | undefined = undefined;
            const schemaTypeNameRec = this.db().getTableByName('schema_type_name').one([
                {
                    fieldName: 'schema_type',
                    value: type
                }
            ]);
            if (!schemaTypeNameRec) {
                console.error(`Failed to create new attribution. Schema type name "${type}" not recognized.`);
                return {
                    data: null,
                    status: 400,
                    error: {
                        message: `Failed to create new attribution. Schema type name "${type}" not recognized.`,
                        code: 'BAD_REQUEST'
                    }
                }
            }
            const schemaTypeRec = this.db().getTableByName('schema_type').one([
                {
                    fieldName: 'schema_type_name_id',
                    value: schemaTypeNameRec.values['id']
                },
                {
                    fieldName: 'latest',
                    value: 1
                }
            ]);
            if (!schemaTypeNameRec) {
                console.error(`Failed to create new attribution. Schema type "${type}" not properly registered.`);
                return {
                    data: null,
                    status: 500,
                    error: {
                        message: `Failed to create new attribution. Schema type "${type}" not properly registered.`,
                        code: 'BAD_REQUEST'
                    }
                }
            }
            const schemaDataRec = this.db().getTableByName('schema_data').createRecord({
                schema_type_id: schemaTypeRec.values['id'],
                data: JSON.stringify(properties)
            });
            const attrRec = this.db().getTableByName('attributions').createRecord({
                schema_data_id: schemaDataRec.values['schema_data_id'],
                display_name: displayName,
                status: status,
                type: type,
            });
            attrGuid = attrRec.values['attribution_guid'];
            return {
                data: attrGuid,
                status: 200
            }
        } catch (e) {
            console.error(`An unexpected error occurred while saving attribution: ${e}`);
            return {
                data: { error: `An unexpected error occurred while saving attribution: ${e}` },
                status: 500,
                error: {
                    message: `An unexpected error occurred while saving the attribution`,
                    code: 'BAD_REQUEST'
                }
            }
        }
    }

    public updateAttribution({ attributionGuid, updateAttributionData }: { attributionGuid: string, updateAttributionData: Record<string, any> }): MockDataForRequestManager {
        try {
            const attrTable = this.db().getTableByName('attributions')
            const attrRec = attrTable.one([{
                fieldName: 'attribution_guid',
                value: attributionGuid
            }]);
            const { properties, ...attrUpdate } = updateAttributionData;
            attrRec.update(this.db(), attrTable, attrUpdate);
            const schemaDataTable = this.db().getTableByName('schema_data');
            const prevSchemaData = schemaDataTable.one([
                {
                    fieldName: 'schema_data_id',
                    value: attrRec.values['schema_data_id']
                },
                {
                    fieldName: 'latest',
                    value: 1
                }
            ]);
            schemaDataTable.createRecord({
                schema_type_id: prevSchemaData.values['schema_type_id'],
                schema_data_id: prevSchemaData.values['schema_data_id'],
                data: JSON.stringify(properties)
            });
            return {
                data: null,
                status: 204
            }
        } catch (e) {
            console.error(`An unexpected error occurred while updating attribution: ${e}`);
            return {
                data: { error: `An unexpected error occurred while updating attribution: ${e}` },
                status: 500,
                error: {
                    message: `An unexpected error occurred while updating the attribution`,
                    code: 'INTERNAL_SERVER'
                }
            }
        }
    }
}